import { yupResolver } from '@hookform/resolvers/yup'
import { selectAddress } from 'feature/addresses/redux/addressCreateSelector'
import { SmsBlockType, SmsStatus } from 'feature/auth/redux/authSlice'
import { RegistrationData } from 'feature/registration/types'
import {
  editUserFormDefaultValues,
  editUserFormDefaultValuesForReset,
  editUserFormSchema,
} from 'feature/user/helpers/editUserFormObjects'
import {
  useEditProfileMutation,
  useEditUserDataConfirmMutation,
  useGetUserQuery,
} from 'feature/user/redux/user.api'
import { GetSmsResponse, UserEditResult } from 'feature/user/types'
import { useSetNotice } from 'hooks/useSetNotice'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useActions, useAppSelector } from 'store/hooks'
import { sendRequestWithAuth } from 'transport/sendRequestWithAuth'
import { Severity } from 'widget/Notifications'
import { SerializedError } from '@reduxjs/toolkit'
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'

export function useUserEditProfile() {
  const [isButtonLoading, setButtonLoading] = useState(false)
  const {
    setInputAddress,
    setInputAddressSuggest,
    setClickMarkCoords,
    setIsValidAddress,
    setCenter,
    setIsDirtyAddress,
  } = useActions()
  const { isLoading, data } = useGetUserQuery()
  const [sendProfile, profileEditData] = useEditProfileMutation()
  const [sendConfirmSms] = useEditUserDataConfirmMutation()
  const { inputSuggest, input } = useAppSelector(selectAddress)
  const setNotice = useSetNotice()
  const navigate = useNavigate()
  const { smsInfo, userInfo } = useAppSelector(state => state.auth)
  const { setSmsInfo, setAuthInfo } = useActions()
  const [hide, setHide] = useState(false)

  const formMethods = useForm<RegistrationData>({
    defaultValues: useMemo(() => editUserFormDefaultValues(data), [data]),
    // @ts-ignore
    resolver: yupResolver(editUserFormSchema),
    mode: 'onChange',
  })

  const checkIsAddressIsNew = () => {
    if (input === data?.customerAddress.address) {
      setIsDirtyAddress(false)
    }
  }

  useEffect(() => {
    setIsValidAddress(true)
    checkIsAddressIsNew()
  }, [])

  useEffect(() => {
    formMethods.reset(editUserFormDefaultValuesForReset(data))
    if (data) {
      setInputAddress(data.customerAddress.address)
      setInputAddressSuggest(data.customerAddress)
      setClickMarkCoords([
        Number(data.customerAddress.geoLat),
        Number(data.customerAddress.geoLon),
      ])
      setCenter([
        Number(data.customerAddress.geoLat),
        Number(data.customerAddress.geoLon),
      ])
    }
  }, [data, formMethods])

  useEffect(() => {
    if (profileEditData?.isError) {
      // @ts-ignore
      const errorText = profileEditData?.error?.data?.message
      setNotice(errorText || 'Ошибка сохранения данных', Severity.error)
    }
  }, [profileEditData])

  useEffect(() => {
    checkIsAddressIsNew()
  }, [input, inputSuggest])

  useEffect(() => {
    return () => {
      setSmsInfo({ status: SmsStatus.readyForSend })
    }
  }, [])

  const onSubmit: () => void = useCallback(async () => {
    const currentValues = formMethods.getValues()

    if (!inputSuggest) throw new Error('onSubmit address')
    setAuthInfo({ isLoading: true })
    sendProfile({
      customerAddress: inputSuggest,
      fatherName: currentValues.middlename,
      lastName: currentValues.lastname,
      firstName: currentValues.firstname,
      newEmail: data?.newEmail || null,
    })
      .then(
        (result: {
          data?: UserEditResult
          error?: FetchBaseQueryError | SerializedError
        }) => {
          if (result.data?.blockedTime !== 0) {
            setHide(true)
            setSmsInfo({
              error: null,
              status: SmsStatus.sendedCode,
              blockTime: result.data?.blockedTime,
              blockType: SmsBlockType.countLimit,
            })
          } else {
            setSmsInfo({
              error: null,
              status: SmsStatus.sendedCode,
              blockTime: result.data.retryAfter,
              blockType: SmsBlockType.timeNotRunout,
            })
          }
        },
      )
      .catch(() => {
        setButtonLoading(false)
      })
      .finally(() => {
        setAuthInfo({ isLoading: false })
      })
  }, [formMethods, sendProfile, inputSuggest])

  const sendSmsConfirm = (code: string) => {
    setAuthInfo({ isLoading: true })
    setSmsInfo({ error: null })
    sendConfirmSms({
      code,
    })
      // @ts-ignore
      .then(({ data: responseData, error: responseError }) => {
        if (responseError) {
          setSmsInfo({ error: responseError.data.message })
          return
        }
        setNotice(responseData.message, Severity.success)
        navigate('/settings')
      })
      .finally(() => {
        setAuthInfo({ isLoading: false })
      })
  }

  const smsBackHandler = () => {
    setSmsInfo({ status: SmsStatus.readyForSend })
  }

  const handlerGetSms = async () => {
    sendRequestWithAuth
      .post<GetSmsResponse>('/v1/customer/change-personal-data-resend')
      .then(({ data: smsData }) => {
        if (smsData.blockedTime !== 0) {
          setSmsInfo({
            error: null,
            status: SmsStatus.sendedCode,
            blockTime: smsData.blockedTime,
            blockType: SmsBlockType.countLimit,
          })
        } else {
          setSmsInfo({
            error: null,
            status: SmsStatus.sendedCode,
            blockTime: smsData.retryAfter,
            blockType: SmsBlockType.timeNotRunout,
          })
        }
      })
      .catch(e => console.error(e))
  }

  return {
    formMethods,
    isLoading,
    isButtonLoading,
    smsInfo,
    userInfo,
    hide,
    setHide,
    onSubmit,
    sendSmsConfirm,
    smsBackHandler,
    handlerGetSms,
    navigate,
  }
}
